import * as React from "react";
import { connect, ConnectedProps } from "react-redux";
import { GlobalApplicationState } from "globalApplicationState";
import { draftEventsApi } from "../../../../api/instances";
import { Event } from "../../models";
import { EDIT_EVENT, VIEW_PUBLISHED_CONTENT_TEXT } from "modules/common/components/authoring/constants";
import { getListPreviewPublishLabel } from "modules/common/components/authoring/utils";
import Preview, { PreviewType } from "modules/common/components/authoring/dialogs/preview";
import EventPreviewContent from "./eventPreviewContent";
import {  ContentType } from "modules/common/models";
import { openContentInPortal } from "utils/redirectUtils";

import '../../../../styles/views.sass'

const EventPreview: React.FC<PropsWithRedux> = ({
    event, 
    show, 
    onClose, 
    isContributor,
    tenantId,
    onPublish,
    isSubmitting,
    isPublishing,
    onEditDetails,
    inEditor
}) => {
    const [previouslyPublishedEventId, setPreviouslyPublishedEventId] = React.useState<string | undefined>(undefined);
    const isPublishedEvent = event.isPreviouslyPublished && event.publishTime ? new Date(event.publishTime) < new Date() : false;

    // no publish action from the editor's own preview; from the Manage Events
    // list, publishing should only be offered for submissions under review.
    const publishLabel = inEditor
        ? undefined
        : getListPreviewPublishLabel(isContributor, !!event.isSubmission);

    React.useEffect(() => {
        const getPreviouslyPublishedEventId = async () => {
            if (show && isPublishedEvent) {
                const publishId = event.id ?
                    (await draftEventsApi.getPublishedFromDraftId(event.id)).data
                    : undefined;
                setPreviouslyPublishedEventId(publishId);
            }
        }

        getPreviouslyPublishedEventId();
    }, [event.id, isPublishedEvent]);

    const getDesktopPreview = () => (
        <div className="event-preview">
            {event && <EventPreviewContent event={event}/>}
        </div>
    );

    const getPreviewOpenAction = (): (() => void) | undefined => {
        return previouslyPublishedEventId ?
            () => openContentInPortal(tenantId, previouslyPublishedEventId, ContentType.Event) :
            undefined;
    };

    return (
        <Preview
            open={show}
            editAction={onEditDetails && (() => onEditDetails())}
            editLabel={onEditDetails && EDIT_EVENT}
            onClose={onClose}
            previews={[{ type: PreviewType.Desktop, label: "DESKTOP", component: getDesktopPreview() }]}
            publishLabel={publishLabel}
            publishAction={() => onPublish && onPublish(true)}
            isLoading={isPublishing || isSubmitting}
            openAction={getPreviewOpenAction()}
            openLabel={VIEW_PUBLISHED_CONTENT_TEXT}
        />
    );
}

interface IEventPreviewProps {
    show: boolean;
    event: Event;
    isContributor: boolean;
    inEditor: boolean;
    onPublish?: (sendNotification: boolean) => void;
    onClose: () => void;
    onEditDetails?: () => void;
}

const connector = connect(
    (state: GlobalApplicationState, ownProps: IEventPreviewProps) => ({
        ...ownProps,
        tenantId: state.tenant.id,
        isSubmitting: state.events.isSubmitting,
        isPublishing: state.events.isPublishing,
    })
);
type PropsWithRedux = ConnectedProps<typeof connector>;

export default connector(EventPreview);
